import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Leaf, Menu, X, LayoutDashboard, LogOut, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { loginWithGoogle } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const links = [
  { to: "/services", label: "Services" },
  { to: "/visualizer", label: "AI Visualizer" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/faq", label: "FAQ" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl backdrop-saturate-150 border-b border-[#E2E8E4]/60 shadow-sm" : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1A362D] text-white transition-transform duration-300 group-hover:scale-105">
              <Leaf className="h-5 w-5" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-xl font-semibold text-[#1A362D] tracking-tight">Master Touch</span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#8FA38C] font-medium">Cleaning & Landscaping</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`text-sm font-medium transition-colors hover:text-[#C47B57] ${
                  location.pathname === l.to ? "text-[#C47B57]" : "text-[#1A362D]"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Button
              onClick={() => navigate("/booking")}
              data-testid="nav-book-now-btn"
              className="bg-[#C47B57] hover:bg-[#D58C68] text-white rounded-full px-6 font-medium"
            >
              Book Now
            </Button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className="rounded-full outline-none ring-offset-2 focus:ring-2 focus:ring-[#8FA38C]">
                    <Avatar className="h-10 w-10 border-2 border-[#E2E8E4]">
                      <AvatarImage src={user.picture} alt={user.name} />
                      <AvatarFallback className="bg-[#1A362D] text-white">
                        {user.name?.[0]?.toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-[#1A362D] truncate">{user.name}</p>
                    <p className="text-xs text-[#8A9A8C] truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/portal")} data-testid="nav-portal-link">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> My Portal
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="nav-admin-link">
                      <Shield className="mr-2 h-4 w-4" /> Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout-btn">
                    <LogOut className="mr-2 h-4 w-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="ghost"
                onClick={() => loginWithGoogle("/portal")}
                data-testid="nav-signin-btn"
                className="text-[#1A362D] hover:text-[#C47B57] hover:bg-transparent font-medium"
              >
                Sign in
              </Button>
            )}
          </div>

          <button
            className="lg:hidden text-[#1A362D] p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#E2E8E4] px-4 py-6 space-y-1" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="block py-3 text-base font-medium text-[#1A362D] border-b border-[#F2F4F2]"
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-4 flex flex-col gap-3">
            <Button onClick={() => navigate("/booking")} className="bg-[#C47B57] hover:bg-[#D58C68] text-white rounded-full w-full">
              Book Now
            </Button>
            {user ? (
              <>
                <Button variant="outline" onClick={() => navigate("/portal")} className="w-full">My Portal</Button>
                {user.is_admin && <Button variant="outline" onClick={() => navigate("/admin")} className="w-full">Admin</Button>}
                <Button variant="ghost" onClick={handleLogout} className="w-full">Sign out</Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => loginWithGoogle("/portal")} className="w-full">Sign in with Google</Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
